import React, { useState, useEffect } from 'react';
import { Settings } from 'lucide-react';
import { Modal } from './Modal';

interface AccessibilityPreferences {
  reduceMotion: boolean;
  highContrast: boolean;
  fontSize: 'normal' | 'large' | 'xlarge';
  focusHighlight: 'normal' | 'high' | 'maximum';
}

const DEFAULT_PREFERENCES: AccessibilityPreferences = {
  reduceMotion: false,
  highContrast: false,
  fontSize: 'normal',
  focusHighlight: 'normal',
};

const STORAGE_KEY = 'accessibility-preferences';

export const AccessibilitySettings: React.FC = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [preferences, setPreferences] = useState<AccessibilityPreferences>(DEFAULT_PREFERENCES);

  // Load preferences from localStorage on mount
  useEffect(() => {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) {
      try {
        const parsed = JSON.parse(stored);
        setPreferences({ ...DEFAULT_PREFERENCES, ...parsed });
      } catch (error) {
        console.error('Failed to parse accessibility preferences:', error);
      }
    }
  }, []);

  // Apply preferences to document
  useEffect(() => {
    const root = document.documentElement;

    // Reduce motion
    if (preferences.reduceMotion) {
      root.style.setProperty('--transition-duration', '0ms');
      root.style.setProperty('--animation-duration', '0ms');
    } else {
      root.style.removeProperty('--transition-duration');
      root.style.removeProperty('--animation-duration');
    }

    // High contrast
    if (preferences.highContrast) {
      root.classList.add('high-contrast');
    } else {
      root.classList.remove('high-contrast');
    }

    // Font size
    root.classList.remove('text-size-normal', 'text-size-large', 'text-size-xlarge');
    root.classList.add(`text-size-${preferences.fontSize}`);

    // Focus highlight
    root.classList.remove('focus-normal', 'focus-high', 'focus-maximum');
    root.classList.add(`focus-${preferences.focusHighlight}`);

    // Save to localStorage
    localStorage.setItem(STORAGE_KEY, JSON.stringify(preferences));
  }, [preferences]);

  const updatePreference = <K extends keyof AccessibilityPreferences>(
    key: K,
    value: AccessibilityPreferences[K]
  ) => {
    setPreferences((prev) => ({ ...prev, [key]: value }));
  };

  const resetToDefaults = () => {
    setPreferences(DEFAULT_PREFERENCES);
  };

  return (
    <>
      <button
        onClick={() => setIsOpen(true)}
        className="mm-btn mm-btn-secondary fixed bottom-4 right-4 z-40 flex items-center gap-2 rounded-full px-4 py-3 text-sm shadow-2xl focus:outline-none focus:ring-2 focus:ring-violet-400"
        aria-label="Open accessibility settings"
        title="Accessibility Settings"
      >
        <Settings size={20} aria-hidden="true" />
        <span className="hidden sm:inline">Accessibility</span>
      </button>

      <Modal
        isOpen={isOpen}
        onClose={() => setIsOpen(false)}
        title="Accessibility Settings"
        description="Customize your accessibility preferences"
        size="md"
        footer={
          <div className="flex justify-end gap-3">
            <button
              onClick={resetToDefaults}
              className="mm-btn mm-btn-secondary px-4 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-violet-400"
            >
              Reset to Defaults
            </button>
            <button
              onClick={() => setIsOpen(false)}
              className="mm-btn mm-btn-primary px-4 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-violet-400"
            >
              Done
            </button>
          </div>
        }
      >
        <div className="space-y-6">
          {/* Reduce Animations */}
          <div className="flex items-start justify-between">
            <div className="flex-1">
              <label
                htmlFor="reduce-motion"
                className="block text-sm font-medium text-white"
              >
                Reduce animations
              </label>
              <p className="mt-1 text-sm text-white/60">
                Minimize motion effects and transitions
              </p>
            </div>
            <div className="ml-4">
              <input
                id="reduce-motion"
                type="checkbox"
                checked={preferences.reduceMotion}
                onChange={(e) => updatePreference('reduceMotion', e.target.checked)}
                className="h-5 w-5 rounded border-white/20 bg-white/5 text-violet-400 focus:ring-2 focus:ring-violet-400 focus:ring-offset-2 touch-target"
                aria-describedby="reduce-motion-description"
              />
            </div>
          </div>

          {/* High Contrast */}
          <div className="flex items-start justify-between">
            <div className="flex-1">
              <label
                htmlFor="high-contrast"
                className="block text-sm font-medium text-white"
              >
                High contrast mode
              </label>
              <p className="mt-1 text-sm text-white/60">
                Increase color contrast for better visibility
              </p>
            </div>
            <div className="ml-4">
              <input
                id="high-contrast"
                type="checkbox"
                checked={preferences.highContrast}
                onChange={(e) => updatePreference('highContrast', e.target.checked)}
                className="h-5 w-5 rounded border-white/20 bg-white/5 text-violet-400 focus:ring-2 focus:ring-violet-400 focus:ring-offset-2 touch-target"
                aria-describedby="high-contrast-description"
              />
            </div>
          </div>

          {/* Font Size */}
          <div>
            <label className="block text-sm font-medium text-white mb-3">
              Font size
            </label>
            <div className="space-y-2" role="radiogroup" aria-label="Font size options">
              {[
                { value: 'normal', label: 'Normal (100%)', description: 'Default text size' },
                { value: 'large', label: 'Large (112.5%)', description: 'Slightly larger text' },
                { value: 'xlarge', label: 'Extra Large (125%)', description: 'Much larger text' },
              ].map((option) => (
                <label
                  key={option.value}
                  className="flex items-center rounded-xl border border-white/10 bg-white/5 p-3 hover:bg-white/10 cursor-pointer"
                >
                  <input
                    type="radio"
                    name="font-size"
                    value={option.value}
                    checked={preferences.fontSize === option.value}
                    onChange={(e) =>
                      updatePreference('fontSize', e.target.value as typeof preferences.fontSize)
                    }
                    className="h-4 w-4 border-white/20 bg-white/5 text-violet-400 focus:ring-2 focus:ring-violet-400 focus:ring-offset-2"
                  />
                  <div className="ml-3 flex-1">
                    <span className="block text-sm font-medium text-white">
                      {option.label}
                    </span>
                    <span className="block text-xs text-white/60">
                      {option.description}
                    </span>
                  </div>
                </label>
              ))}
            </div>
          </div>

          {/* Focus Highlight Intensity */}
          <div>
            <label className="block text-sm font-medium text-white mb-3">
              Focus highlight intensity
            </label>
            <div className="space-y-2" role="radiogroup" aria-label="Focus highlight options">
              {[
                { value: 'normal', label: 'Normal', description: 'Standard focus indicators' },
                { value: 'high', label: 'High', description: 'More prominent focus rings' },
                {
                  value: 'maximum',
                  label: 'Maximum',
                  description: 'Very prominent focus indicators',
                },
              ].map((option) => (
                <label
                  key={option.value}
                  className="flex items-center rounded-xl border border-white/10 bg-white/5 p-3 hover:bg-white/10 cursor-pointer"
                >
                  <input
                    type="radio"
                    name="focus-highlight"
                    value={option.value}
                    checked={preferences.focusHighlight === option.value}
                    onChange={(e) =>
                      updatePreference(
                        'focusHighlight',
                        e.target.value as typeof preferences.focusHighlight
                      )
                    }
                    className="h-4 w-4 border-white/20 bg-white/5 text-violet-400 focus:ring-2 focus:ring-violet-400 focus:ring-offset-2"
                  />
                  <div className="ml-3 flex-1">
                    <span className="block text-sm font-medium text-white">
                      {option.label}
                    </span>
                    <span className="block text-xs text-white/60">
                      {option.description}
                    </span>
                  </div>
                </label>
              ))}
            </div>
          </div>

          {/* Info text */}
          <div className="rounded-xl border border-violet-500/20 bg-white/5 p-4">
            <p className="text-sm text-white/70">
              These settings are saved locally in your browser and will persist across sessions.
            </p>
          </div>
        </div>
      </Modal>
    </>
  );
};

export default AccessibilitySettings;
